import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Card, KPI, Empty } from "@/components/Primitives";
import { Users, Briefcase, AlertTriangle, Zap } from "lucide-react";

function money(v, c = "GBP") {
  const s = c === "GBP" ? "£" : c === "USD" ? "$" : c + " ";
  const n = v ?? 0;
  const hasPence = Math.round(n * 100) !== Math.round(n) * 100;
  return `${s}${n.toLocaleString(undefined, { minimumFractionDigits: hasPence ? 2 : 0, maximumFractionDigits: 2 })}`;
}

function utilColor(pct) {
  if (pct >= 100) return { bar: "#F43F5E", text: "text-rose-600 dark:text-rose-400", label: "Overloaded" };
  if (pct >= 80)  return { bar: "#F59E0B", text: "text-amber-600 dark:text-amber-400", label: "High" };
  if (pct >= 40)  return { bar: "hsl(var(--primary))", text: "text-[hsl(var(--primary))]", label: "Healthy" };
  if (pct > 0)    return { bar: "#10B981", text: "text-emerald-600 dark:text-emerald-400", label: "Available" };
  return { bar: "hsl(var(--text-muted))", text: "text-[hsl(var(--text-muted))]", label: "Idle" };
}

export default function ResourcePage() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/stats/resource-allocation");
        setData(data);
      } catch (e) { console.error(e); }
    })();
  }, []);

  if (!data) return <div className="text-sm text-muted">Loading…</div>;

  const { members, totals } = data;
  const filtered = members.filter((m) => {
    if (filter === "all") return true;
    if (filter === "internal") return (m.type || "internal") === "internal";
    if (filter === "external") return m.type === "external";
    if (filter === "overloaded") return m.utilization_pct >= 100;
    if (filter === "available") return m.utilization_pct < 40;
    return true;
  });

  const overloaded = members.filter((m) => m.utilization_pct >= 100).length;
  const idle = members.filter((m) => m.utilization_pct === 0).length;

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb="Workspace · Resource"
        title="Resource allocation"
        subtitle="Balance workload across your team. Internal and external."
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Members" value={totals.members} sub={`${totals.internal} internal · ${totals.external} external`} variant="primary" />
        <KPI label="Weekly capacity" value={`${totals.total_capacity_hours}h`} sub="Across team" />
        <KPI label="Allocated" value={`${totals.total_remaining_hours}h`} sub="Remaining on open tasks" variant={totals.total_remaining_hours > totals.total_capacity_hours ? "warning" : "default"}/>
        <KPI label="Committed cost" value={money(totals.total_committed_cost)} sub="Of open work" />
      </div>

      {(overloaded > 0 || idle > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {overloaded > 0 && (
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-rose-500/15 grid place-items-center text-rose-600 dark:text-rose-400">
                <AlertTriangle className="w-5 h-5" strokeWidth={2}/>
              </div>
              <div>
                <div className="font-semibold text-[14px]">{overloaded} {overloaded === 1 ? "person is" : "people are"} overloaded</div>
                <div className="text-[12px] text-[hsl(var(--text-muted))]">Reassign tasks or raise capacity.</div>
              </div>
            </div>
          )}
          {idle > 0 && (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/15 grid place-items-center text-emerald-600 dark:text-emerald-400">
                <Zap className="w-5 h-5" strokeWidth={2}/>
              </div>
              <div>
                <div className="font-semibold text-[14px]">{idle} {idle === 1 ? "person is" : "people are"} idle</div>
                <div className="text-[12px] text-[hsl(var(--text-muted))]">Capacity available. Consider assigning open work.</div>
              </div>
            </div>
          )}
        </div>
      )}

      <Card>
        <div className="px-5 py-3 flex items-center gap-3 border-b border-[hsl(var(--border))]">
          {["all", "internal", "external", "overloaded", "available"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-[12px] font-semibold px-3 py-1.5 rounded-md transition-all ${filter === f ? "bg-[hsl(var(--primary))] text-white" : "text-[hsl(var(--text-muted))] hover:bg-[hsl(var(--surface-hover))]"}`}
              data-testid={`resource-filter-${f}`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
          <div className="ml-auto text-[12px] text-[hsl(var(--text-muted))]">{filtered.length} {filtered.length === 1 ? "member" : "members"}</div>
        </div>

        <div className="divide-y divide-[hsl(var(--border))]">
          {filtered.map((m) => {
            const u = utilColor(m.utilization_pct);
            const capped = Math.min(m.utilization_pct, 150);
            const isExt = m.type === "external";
            return (
              <div key={m.member_id} className="px-5 py-4 flex items-center gap-4" data-testid="resource-row">
                <img src={m.avatar_url || "/brand/soubor-one-mark.png"} alt="" onError={(e) => { e.currentTarget.src = "/brand/soubor-one-mark.png"; }} className="w-12 h-12 rounded-full object-cover ring-2 ring-[hsl(var(--border))]"/>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-[14px] truncate">{m.name}</div>
                    <span className={`text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded ${isExt ? "bg-amber-500/15 text-amber-600 dark:text-amber-400" : "bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))]"}`}>
                      {isExt ? "External" : "Internal"}
                    </span>
                    {!m.active && <span className="text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))]">Inactive</span>}
                  </div>
                  <div className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5">{m.role_title}</div>

                  <div className="mt-2 flex items-baseline gap-3 flex-wrap">
                    <span className="font-mono font-semibold text-[13px]">{m.remaining_hours}h</span>
                    <span className="text-[11px] text-[hsl(var(--text-muted))]">of {m.weekly_capacity_hours}h capacity</span>
                    <span className={`text-[11px] font-semibold ${u.text}`}>· {u.label} ({m.utilization_pct}%)</span>
                  </div>

                  <div className="mt-2 h-1.5 rounded-full bg-[hsl(var(--surface-hover))] overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${Math.max(0, Math.min(100, capped))}%`, background: u.bar }}
                    />
                  </div>
                </div>

                <div className="shrink-0 text-right hidden md:block">
                  <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">Rate</div>
                  <div className="font-mono font-semibold text-[13px]">{m.hourly_rate > 0 ? `${money(m.hourly_rate, m.currency)}/hr` : "—"}</div>
                </div>

                <div className="shrink-0 text-right hidden md:block">
                  <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">Open tasks</div>
                  <div className="font-mono font-semibold text-[13px] inline-flex items-center gap-1">
                    <Briefcase className="w-3.5 h-3.5 text-[hsl(var(--text-muted))]" strokeWidth={1.6}/>{m.open_task_count}
                  </div>
                </div>

                <div className="shrink-0 text-right hidden lg:block min-w-[100px]">
                  <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">Committed</div>
                  <div className="font-mono font-semibold text-[13px]">{money(m.committed_cost, m.currency)}</div>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && <Empty title="No team members match" subtitle="Try another filter."/>}
        </div>
      </Card>
    </div>
  );
}
